/**
 * DashboardPage - Main dashboard for Studio Pass Pro
 * Refactored to use modular components with cursor-based pagination
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { imagesAPI, paymentAPI, downloadsAPI, announcementsAPI, authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PreviewModal from "@/components/PreviewModal";
import { trackMetaEvent } from "@/lib/meta";
import { trackInternalEvent } from "@/lib/analytics";
import {
  LogOut, User, Download, Search, Loader2, Upload, X,
  Sparkles, Crown, History, Images, LayoutDashboard, ChevronDown
} from "lucide-react";

// Import dashboard components
import {
  PlanDetailsModal,
  ImageCard,
  DownloadCard,
  Pagination,
  UpgradeBanner,
  AnnouncementBanner,
  ScrollToTop,
  OnboardingModal,
  FreeSampleProgress,
  OnboardingStrip,
  PostPaymentModal,
  ProfileDropdown,
  BillingPanel,
} from "@/components/dashboard";

export default function DashboardPage() {
  const { user, logout, checkAuth, waContext } = useAuth();
  const navigate = useNavigate();

  // Image library state with cursor-based pagination
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const debounceRef = useRef(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Restore pending download from homepage OTP gate (browse-first flow).
  // Runs once on mount BEFORE the initial fetch so the correct search is used.
  const pendingRestoredRef = useRef(false);
  useEffect(() => {
    if (pendingRestoredRef.current) return;
    try {
      const raw = sessionStorage.getItem("spp_pending_download");
      if (!raw) return;
      const pending = JSON.parse(raw);
      sessionStorage.removeItem("spp_pending_download");
      pendingRestoredRef.current = true;
      if (pending?.search) {
        setSearch(pending.search);
        setSearchInput(pending.search);
      }
      if (pending?.previewImage?.public_id) {
        // Reopen preview modal for the previously selected image.
        setPreviewImage(pending.previewImage);
        trackInternalEvent("PendingDownloadRestored", {
          user_id: user?.user_id,
          product_id: pending.previewImage.public_id,
          metadata: { search: pending.search || "" },
        });
        toast.info("Continue where you left off — click Download to get your free sample.", { duration: 4500 });
      }
    } catch (_) {
      // Bad JSON — ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Track seen public_ids to prevent duplicates
  const seenIdsRef = useRef(new Set());

  // Multi-select filters
  const [vegOnly, setVegOnly] = useState(false);
  const [fillerImages, setFillerImages] = useState([]);

  // Tabs
  const [activeTab, setActiveTab] = useState("library");

  // Upgrade modal & banner
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showBillingPanel, setShowBillingPanel] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showPostPayment, setShowPostPayment] = useState(false);

  // WA magic link: auto-open upgrade popup
  useEffect(() => {
    const shouldUpgrade =
      (waContext?.cta_destination === "upgrade") ||
      sessionStorage.getItem("spp_show_upgrade") === "true";
    if (shouldUpgrade && user) {
      setShowPlanModal(true);
      sessionStorage.removeItem("spp_show_upgrade");
      // Track WA event
      if (waContext?.wa_token) {
        authAPI.trackWaEvent(waContext.wa_token, "upgrade_popup_opened_from_wa").catch(() => {});
      }
    }
  }, [waContext, user]);

  // Derived values (needed before hooks below)
  const displayName = user?.display_name || `+91 ${user?.phone?.slice(3)}`;
  const remainingDownloads =
    user?.remaining_downloads ??
    (user?.free_download_limit ?? 2) - (user?.downloads_used ?? 0);
  const isFree = user?.plan !== "paid";
  const userPlanId = user?.plan_id || null;
  const showPremiumLock = isFree || userPlanId === "starter";
  const isAdmin = user?.role === "admin";

  // Smart upgrade triggers (behavioral tracking)
  const imagesOpenedRef = useRef(0);
  const searchCountRef = useRef(0);
  const scrollTriggeredRef = useRef(false);
  const exitIntentTriggeredRef = useRef(false);
  const hasSeenUpgradeRef = useRef(
    sessionStorage.getItem("spp_upgrade_shown") === "true"
  );

  const triggerSmartUpgrade = useCallback(() => {
    if (hasSeenUpgradeRef.current) return;
    // Only auto-trigger pricing if user has exhausted free downloads
    const used = user?.downloads_used ?? 0;
    const limit = user?.free_download_limit ?? 2;
    if (used < limit) return;
    hasSeenUpgradeRef.current = true;
    sessionStorage.setItem("spp_upgrade_shown", "true");
    setShowPlanModal(true);
    trackInternalEvent("UpgradePopupShown", {
      user_id: user?.user_id,
      metadata: { trigger: "auto", downloads_used: used, limit },
    });
  }, [user?.downloads_used, user?.free_download_limit, user?.user_id]);

  // Handle image card click — intercept locked images for free/starter users
  const handleImageClick = useCallback(
    (img) => {
      if (showPremiumLock && img.is_premium) {
        // Locked image → always show upgrade, bypass preview
        setShowPlanModal(true);
        trackInternalEvent("UpgradePopupShown", {
          user_id: user?.user_id,
          metadata: { trigger: "premium_locked_image", image_id: img.public_id },
        });
        trackInternalEvent("UpgradePopupClicked", {
          user_id: user?.user_id,
          metadata: { source: "premium_locked_image", image_id: img.public_id },
        });
        return;
      }
      // Track image opens for free users
      if (isFree) {
        imagesOpenedRef.current += 1;
        if (imagesOpenedRef.current >= 5) triggerSmartUpgrade();
      }
      trackInternalEvent("PreviewOpened", {
        user_id: user?.user_id,
        product_id: img.public_id,
        metadata: { dish_name: img.dish_name, is_premium: !!img.is_premium },
      });
      setPreviewImage(img);
    },
    [isFree, showPremiumLock, triggerSmartUpgrade, user?.user_id]
  );

  // Track search count
  const handleSearchTracked = useCallback(
    (value) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearch(value);
        if (value.trim()) {
          trackInternalEvent("SearchPerformed", {
            user_id: user?.user_id,
            metadata: { keyword: value.trim(), is_free_user: isFree },
          });
        }
        if (isFree && value.trim()) {
          searchCountRef.current += 1;
          if (searchCountRef.current >= 3) triggerSmartUpgrade();
        }
      }, 350);
    },
    [isFree, triggerSmartUpgrade]
  );

  // Scroll depth trigger (60%, once per session)
  useEffect(() => {
    if (!isFree || hasSeenUpgradeRef.current) return;
    const handleScroll = () => {
      if (scrollTriggeredRef.current) return;
      const scrollPct =
        (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (scrollPct > 0.6) {
        scrollTriggeredRef.current = true;
        triggerSmartUpgrade();
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFree, triggerSmartUpgrade]);

  // Exit intent trigger (desktop only, once)
  useEffect(() => {
    if (!isFree || hasSeenUpgradeRef.current) return;
    const handleMouseLeave = (e) => {
      if (exitIntentTriggeredRef.current) return;
      if (e.clientY <= 5) {
        exitIntentTriggeredRef.current = true;
        triggerSmartUpgrade();
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [isFree, triggerSmartUpgrade]);

  // Download-based trigger (≥2 downloads used)
  useEffect(() => {
    if (!isFree || hasSeenUpgradeRef.current) return;
    if ((user?.downloads_used ?? 0) >= 2) {
      triggerSmartUpgrade();
    }
  }, [isFree, user?.downloads_used, triggerSmartUpgrade]);

  // My Downloads
  const [downloadHistory, setDownloadHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [announcementDismissed, setAnnouncementDismissed] = useState(() => {
    return sessionStorage.getItem("spp_announcement_dismissed") === "true";
  });

  // CSV upload (admin only)
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleLogout = async () => {
    await logout();
    // Logout deliberately returns to the public homepage. The Sign In modal
    // must NOT auto-open after an intentional logout.
    navigate("/", { replace: true });
  };

  // Fetch images with cursor-based pagination
  const fetchImages = useCallback(
    async (currentSearch, vegFilter, cursor = null, append = false) => {
      // Set appropriate loading state
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      try {
        const params = { limit: 30 };
        if (currentSearch.trim()) {
          params.search = currentSearch.trim();
          // For search, cursor is negative page number (e.g. -2 = page 2)
          if (cursor !== null && cursor < 0) {
            params.page = Math.abs(cursor);
          }
        } else {
          // Non-search: use cursor-based pagination
          if (cursor !== null && cursor >= 0) params.cursor = cursor;
        }
        if (vegFilter) params.veg_only = true;
        
        const res = await imagesAPI.list(params);
        const newImages = res.data.images || [];
        
        if (append) {
          // Filter out duplicates using seenIds
          const uniqueNewImages = newImages.filter(img => {
            if (seenIdsRef.current.has(img.public_id)) {
              return false;
            }
            seenIdsRef.current.add(img.public_id);
            return true;
          });
          setImages(prev => [...prev, ...uniqueNewImages]);
        } else {
          // Reset: clear seen IDs and set fresh images
          seenIdsRef.current.clear();
          newImages.forEach(img => seenIdsRef.current.add(img.public_id));
          setImages(newImages);
        }
        
        setNextCursor(res.data.next_cursor);
        setTotal(res.data.total);
        setFillerImages(res.data.filler_images || []);
      } catch {
        toast.error("Failed to load images");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Fetch filter options and announcements once
  useEffect(() => {
    announcementsAPI
      .list()
      .then((res) => setAnnouncements(res.data))
      .catch(() => {});
  }, []);

  // Fetch images when filters/search change (reset list)
  useEffect(() => {
    fetchImages(
      search,
      vegOnly,
      null,  // Start from beginning
      false, // Replace, not append
    );
  }, [search, vegOnly, fetchImages]);

  // Load more images using cursor
  const handleLoadMore = () => {
    if (nextCursor !== null && !loadingMore) {
      fetchImages(
        search,
        vegOnly,
        nextCursor,
        true,  // Append to existing
      );
    }
  };

  // Download history
  const fetchDownloadHistory = useCallback(async (p = 1) => {
    setHistoryLoading(true);
    try {
      const res = await downloadsAPI.history({ page: p, limit: 30 });
      setDownloadHistory(res.data);
    } catch {
      toast.error("Failed to load download history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "downloads") {
      fetchDownloadHistory(historyPage);
    }
  }, [activeTab, historyPage, fetchDownloadHistory]);

  // Payment success handler — called by PlanDetailsModal after successful payment
  const handlePaymentSuccess = () => {
    checkAuth();
    setShowPostPayment(true);
  };

  // CSV upload handler
  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await imagesAPI.uploadCSV(file);
      const d = res.data;
      toast.success(
        `CSV imported: ${d.inserted} new, ${d.updated} updated, ${d.skipped} skipped`
      );
      imagesAPI
        .filters()
        .then((res) => setFilterOptions(res.data))
        .catch(() => {});
      // Reset and reload images from beginning
      fetchImages(search, cuisineFilters.join(","), categoryFilters.join(","), null, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Handle announcement dismiss
  const handleAnnouncementDismiss = () => {
    setAnnouncementDismissed(true);
    sessionStorage.setItem("spp_announcement_dismissed", "true");
  };

  // Get active announcement
  const activeAnnouncement = !announcementDismissed && announcements.length > 0
    ? announcements.find((a) => {
        const target = a.target_audience || "all";
        return target === "all" || target === user?.plan;
      })
    : null;

  // Clear filters
  const clearAllFilters = () => {
    setVegOnly(false);
    setSearchInput("");
    setSearch("");
  };

  const hasActiveFilters = vegOnly || search.trim();

  // Auto-scroll to image grid
  const scrollToGrid = () => {
    setTimeout(() => {
      const grid = document.getElementById("image-grid");
      if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_57dcb951-2bbd-452d-9917-248d638c6a0f/artifacts/2qmwtxby_Logo%20food%20photos%20%286%29.png"
              alt="Studio Pass 2.0"
              className="h-10 sm:h-12 w-[180px] sm:w-[220px] object-cover object-center"
              data-testid="dashboard-logo"
            />
            {!isFree && (
              <Badge
                variant="secondary"
                className="h-5 px-1.5 text-[10px] font-semibold bg-amber-100 text-amber-700 border-0"
              >
                PRO
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-3">
            {isAdmin && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/admin")}
                  className="hidden sm:flex items-center gap-1.5 text-xs border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                  data-testid="admin-dashboard-btn"
                >
                  <LayoutDashboard className="h-3.5 w-3.5" />
                  Admin Dashboard
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCSVUpload}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="hidden sm:flex items-center gap-1.5 text-xs"
                  data-testid="csv-upload-btn"
                >
                  {uploading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  {uploading ? "Uploading..." : "Upload CSV"}
                </Button>
              </>
            )}
            <ProfileDropdown
              user={user}
              isAdmin={isAdmin}
              displayName={displayName}
              onLogout={handleLogout}
              onUpgrade={() => setShowPlanModal(true)}
              onDownloadsTab={() => setActiveTab("downloads")}
              onBillingClick={() => setShowBillingPanel(true)}
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Announcement Banner - PRO users only */}
        {!isFree && activeAnnouncement && (
          <div className="mb-6">
            <AnnouncementBanner
              announcement={activeAnnouncement}
              onDismiss={handleAnnouncementDismiss}
            />
          </div>
        )}

        {/* Status Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            {remainingDownloads > 0 ? (
              <span className="text-xs text-slate-400" data-testid="downloads-remaining">
                {isFree
                  ? `${remainingDownloads} free sample download${remainingDownloads !== 1 ? "s" : ""} remaining`
                  : `${remainingDownloads} download${remainingDownloads !== 1 ? "s" : ""} remaining`}
              </span>
            ) : (
              <span className="text-xs text-red-400" data-testid="downloads-remaining">
                {isFree ? "0 free sample downloads remaining" : "0 downloads remaining"}
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6">
          <button
            onClick={() => setActiveTab("library")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "library"
                ? "border-[#18181B] text-[#09090B]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-library"
          >
            <Images className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            Image Library
          </button>
          <button
            onClick={() => setActiveTab("downloads")}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "downloads"
                ? "border-[#18181B] text-[#09090B]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-downloads"
          >
            <History className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
            My Downloads
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "library" ? (
          <div data-testid="library-tab">
            {/* Search & Filters - Sticky Container */}
            <div className="sticky top-14 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-[#FAFAFA]/95 backdrop-blur-sm border-b border-slate-200/80 mb-4" data-testid="sticky-search-bar">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search dishes..."
                    value={searchInput}
                    onChange={(e) => handleSearchTracked(e.target.value)}
                    className="pl-10 h-10 text-sm bg-white"
                    data-testid="search-input"
                  />
                </div>

                {/* Veg Only Toggle */}
                <label
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none transition-colors ${
                    vegOnly
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                  data-testid="veg-only-toggle"
                  onClick={() => { setVegOnly(!vegOnly); scrollToGrid(); }}
                >
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors ${
                      vegOnly ? "bg-emerald-500" : "bg-slate-200"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                        vegOnly ? "translate-x-4" : ""
                      }`}
                    />
                  </div>
                  <span className="text-xs font-medium whitespace-nowrap">Veg Only</span>
                </label>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="text-xs text-slate-500 hover:text-slate-700"
                    data-testid="clear-filters-btn"
                  >
                    <X className="h-3.5 w-3.5 mr-1" />
                    Clear all
                  </Button>
                )}
              </div>

              {/* Download Progress - visible for all users */}
              {user && (
                <div className="mt-2.5">
                  <FreeSampleProgress
                    downloadsUsed={user?.downloads_used ?? 0}
                    freeLimit={user?.free_download_limit ?? 2}
                    onUpgradeClick={() => setShowPlanModal(true)}
                    planId={userPlanId}
                  />
                  {isFree && <OnboardingStrip downloadsUsed={user?.downloads_used ?? 0} />}
                </div>
              )}
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500" data-testid="results-count">
                {loading ? "Loading..." : ""}
              </p>
            </div>

            {/* Image Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : images.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-slate-500">No images found</p>
                {hasActiveFilters && (
                  <Button
                    variant="link"
                    onClick={clearAllFilters}
                    className="mt-2 text-sm"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                  data-testid="image-grid"
                  id="image-grid"
                >
                  {images.map((img) => (
                    <ImageCard
                      key={img.public_id}
                      image={img}
                      onClick={handleImageClick}
                      isFree={showPremiumLock}
                    />
                  ))}
                  {/* Filler premium cards for free/starter users on sparse search */}
                  {showPremiumLock && fillerImages.length > 0 && fillerImages.map((img) => (
                    <ImageCard
                      key={`filler-${img.public_id}`}
                      image={{...img, is_premium: true}}
                      onClick={handleImageClick}
                      isFree={true}
                    />
                  ))}
                </div>
                
                {/* Load More Button */}
                {nextCursor !== null && (
                  <div className="flex justify-center mt-8 mb-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="min-w-[200px] h-12 text-sm font-medium border-slate-200 hover:bg-slate-50"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-2" />
                          Load More Images
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* No more images indicator - hide when filler cards are showing */}
                {nextCursor === null && images.length > 0 && !loading && !(showPremiumLock && fillerImages.length > 0) && (
                  <div className="text-center mt-8 mb-4">
                    <p className="text-sm text-slate-400">You've reached the end</p>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div data-testid="downloads-tab">
            {/* Downloads Stats */}
            {downloadHistory && (
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Unique this month</p>
                  <p className="text-xl font-bold text-[#09090B]">
                    {downloadHistory.unique_this_month}
                  </p>
                </div>
                <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
                  <p className="text-xs text-slate-500 mb-1">Total downloads</p>
                  <p className="text-xl font-bold text-[#09090B]">
                    {downloadHistory.total}
                  </p>
                </div>
              </div>
            )}

            {/* Downloads Grid */}
            {historyLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : !downloadHistory || downloadHistory.downloads.length === 0 ? (
              <div className="text-center py-20">
                <Download className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No downloads yet</p>
                <Button
                  variant="link"
                  onClick={() => setActiveTab("library")}
                  className="mt-2 text-sm"
                >
                  Browse the library
                </Button>
              </div>
            ) : (
              <>
                <div
                  className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
                  data-testid="downloads-grid"
                >
                  {downloadHistory.downloads.map((dl, i) => (
                    <DownloadCard
                      key={`${dl.public_id}-${dl.timestamp}-${i}`}
                      download={dl}
                    />
                  ))}
                </div>
                {downloadHistory.total_pages > 1 && (
                  <Pagination
                    page={historyPage}
                    totalPages={downloadHistory.total_pages}
                    onPageChange={(p) => {
                      setHistoryPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Bottom spacer when upgrade banner is visible */}
      {user?.downloads_used > 0 && !bannerDismissed && (
        <div className="h-24" />
      )}

      {/* Upgrade Banner (plan-aware for all users) */}
      {user?.downloads_used > 0 && (
        <UpgradeBanner
          downloadsUsed={user.downloads_used}
          freeDownloadLimit={user.free_download_limit || 2}
          onUpgradeClick={() => setShowPlanModal(true)}
          onDismiss={() => setBannerDismissed(true)}
          dismissed={bannerDismissed}
          planId={userPlanId}
        />
      )}

      {/* Plan Details Modal */}
      {showPlanModal && (
        <PlanDetailsModal
          user={user}
          onClose={() => setShowPlanModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
          waContext={waContext}
        />
      )}

      {showBillingPanel && (
        <BillingPanel
          user={user}
          onClose={() => setShowBillingPanel(false)}
          onUpgrade={() => { setShowBillingPanel(false); setShowPlanModal(true); }}
        />
      )}

      {/* Preview Modal */}
      {previewImage && (
        <PreviewModal
          image={previewImage}
          images={images}
          onClose={() => setPreviewImage(null)}
          onNavigate={setPreviewImage}
          user={user}
          onUserUpdate={checkAuth}
          onShowPricing={() => setShowPlanModal(true)}
          showPremiumLock={showPremiumLock}
        />
      )}

      {/* Scroll to Top - only on library tab */}
      {activeTab === "library" && <ScrollToTop threshold={800} />}

      {/* Onboarding / Activation Modal — shown once per session for free users */}
      {isFree && (
        <OnboardingModal
          user={user}
          remainingDownloads={remainingDownloads}
          onClose={() => {}}
          onUpgradeClick={() => setShowPlanModal(true)}
          onStartExploring={scrollToGrid}
        />
      )}

      {/* Post-payment success modal */}
      {showPostPayment && (
        <PostPaymentModal onClose={() => setShowPostPayment(false)} />
      )}
    </div>
  );
}
